/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react';
import braintree from 'braintree-web';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useGetConfigQuery } from 'renderer/features/purchase-page/services/purchase.endpoints';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { Country, State } from 'country-state-city';
import { CheckboxWithLabel } from 'formik-mui';
import StripeCreditCardForm from 'renderer/features/purchase-page/components/StripeCreditCardForm';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import PaymentMethodForm from 'renderer/features/purchase-page/components/PaymentMethodForm';
import BillingAddressForm from './BillingAddressForm';
import {
  useCreatePaymentMethodMutation,
  useEditPaymentMethodMutation,
} from '../services/paymentMethods.endpoints';
import {
  EditBillingAddressFormSchema,
  NewPaymentMethodFormSchema,
  PaymentMethod,
} from '../schemas/paymentMethods.schemas';
// import {
//   useGetConfigQuery,
//   useMakePurchaseMutation,
// } from '../services/purchase.endpoints';

const initialForm = {
  newPaymentMethodToken: '',
  nameOnCard: '',
  streetAddress: '',
  locality: '',
  region: '',
  regionCode: '',
  postalCode: '',
  countryName: '',
  countryCode: '',
  numberValidity: '',
  cvvValidity: '',
  expirationDateValidity: '',
  addNewCard: true,
  provider: '',
};

interface SuccessListenerProps {
  isSuccess: boolean;
}

const SuccessListener: React.FC<SuccessListenerProps> = ({ isSuccess }) => {
  const { resetForm } = useFormikContext();

  useEffect(() => {
    if (isSuccess) {
      resetForm();
    }
  }, [isSuccess, resetForm]);

  return null;
};

interface ExistingCardListenerProps {
  mode: 'edit' | 'create';
  card?: PaymentMethod;
}

const ExistingCardListener: React.FC<ExistingCardListenerProps> = ({
  card,
  mode,
}) => {
  const { setFieldValue, resetForm } = useFormikContext<typeof initialForm>();

  useEffect(() => {
    if (mode === 'edit' && card) {
      const countryCode = Country.getAllCountries().find(
        ({ name }) => card.billingAddress.countryName === name,
      )?.isoCode;
      const regionCode = State.getStatesOfCountry(countryCode ?? '').find(
        (state) => state.name === card.billingAddress.region,
      )?.isoCode;
      setFieldValue('newPaymentMethodToken', '');
      setFieldValue('nameOnCard', card.billingAddress.nameOnCard);
      setFieldValue('streetAddress', card.billingAddress.streetAddress);
      setFieldValue('locality', card.billingAddress.locality);
      setFieldValue('postalCode', card.billingAddress.postalCode);
      setFieldValue('countryCode', countryCode ?? '');
      setFieldValue('regionCode', regionCode ?? '');
    }
  }, [card, mode, setFieldValue]);

  useEffect(() => {
    if (mode === 'create') {
      resetForm();
    }
  }, [mode, resetForm]);

  return null;
};

interface AddCardDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  userId: string;
  card?: PaymentMethod;
}

const AddCardDialog: React.FC<AddCardDialogProps> = ({
  open,
  onClose,
  mode,
  userId,
  card,
}) => {
  const { data: config } = useGetConfigQuery();
  const [hostedFieldsInstance, setHostedFieldsInstance] =
    useState<braintree.HostedFields | null>(null);
  const [
    createPaymentMethod,
    {
      isLoading: createLoading,
      error: createError,
      isSuccess: createSuccess,
      reset: resetCreate,
    },
  ] = useCreatePaymentMethodMutation();
  const [
    editPaymentMethod,
    {
      isLoading: editLoading,
      isSuccess: editSuccess,
      error: editError,
      reset: resetEdit,
    },
  ] = useEditPaymentMethodMutation();
  const isLoading = createLoading || editLoading;
  const [tokenError, setTokenError] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const [formSubmitted, setFormSubmitted] = useState(false); // Track form submission

  // Handle closing and resetting errors
  const handleClose = () => {
    setTokenError('');
    resetCreate();
    resetEdit();
    onClose();
  };

  // Reset errors only when dialog opens for the first time
  useEffect(() => {
    if (open && !formSubmitted) {
      setTokenError('');
      resetCreate();
      resetEdit();
    }
  }, [open, formSubmitted, resetCreate, resetEdit]);

  useEffect(() => {
    if (createSuccess) {
      resetCreate();
      onClose();
    }
  }, [createSuccess, onClose, resetCreate]);

  useEffect(() => {
    if (editSuccess && !open) {
      resetEdit();
      // onClose();
    }
  }, [editSuccess, open, resetEdit]);

  const submitCreateForm = async (values: typeof initialForm) => {
    setFormSubmitted(true);
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      return;
    }

    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    const { paymentMethod } = result;

    await createPaymentMethod({
      provider: 'stripe',
      billingAddress: {
        nameOnCard: values.nameOnCard,
        countryName: values.countryName,
        streetAddress: values.streetAddress,
        locality: values.locality,
        region: values.region,
        postalCode: values.postalCode,
      },
      userId,
      token: paymentMethod?.id ?? '',
    });
  };

  const submitEditForm = async (values: typeof initialForm) => {
    setFormSubmitted(true);
    if (!card) return;
    const { provider, addNewCard } = values;
    if (card.braintreeId && hostedFieldsInstance && addNewCard) {
      // If editing a Braintree card and new card information is entered
      try {
        const { nonce } = await hostedFieldsInstance.tokenize();
        await editPaymentMethod({
          id: card._id,
          payload: {
            provider: 'braintree',
            userId,
            billingAddress: {
              nameOnCard: values.nameOnCard,
              countryName: values.countryName,
              streetAddress: values.streetAddress,
              locality: values.locality,
              region: values.region,
              postalCode: values.postalCode,
            },
            token: nonce, // Use the new nonce for the updated card
          },
        });
      } catch (error) {
        setTokenError(
          'There was an error updating the card details. Please try again.',
        );
        console.error(error);
      }
    } else if (card.stripeId && stripe && elements) {
      // If editing a Stripe card and new card information is entered
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        console.error('CardNumberElement not found.');
        return;
      }
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      const { paymentMethod } = result;

      await editPaymentMethod({
        id: card._id,
        payload: {
          provider: 'stripe',
          userId,
          default: true,
          billingAddress: {
            nameOnCard: values.nameOnCard,
            countryName: values.countryName,
            streetAddress: values.streetAddress,
            locality: values.locality,
            region: values.region,
            postalCode: values.postalCode,
          },
          token: paymentMethod?.id ?? '', // Use the new Stripe token
        },
      });
    } else if (!addNewCard) {
      // If only editing the billing address
      await editPaymentMethod({
        id: card._id,
        payload: {
          provider: card.stripeId ? 'stripe' : 'braintree',
          userId,
          billingAddress: {
            nameOnCard: values.nameOnCard,
            countryName: values.countryName,
            streetAddress: values.streetAddress,
            locality: values.locality,
            region: values.region,
            postalCode: values.postalCode,
          },
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={{}}>
      <Formik
        initialValues={initialForm}
        validate={(values) => {
          const formSchema =
            mode === 'create' || values.addNewCard
              ? NewPaymentMethodFormSchema
              : EditBillingAddressFormSchema;
          return toValidator(formSchema)(values);
        }}
        onSubmit={async (values) => {
          if (mode === 'create') {
            submitCreateForm(values);
          }
          if (mode === 'edit') {
            submitEditForm(values);
          }
        }}
      >
        {({ values }) => {
          return (
            <Form>
              <SuccessListener isSuccess={createSuccess} />
              <ExistingCardListener mode={mode} card={card} />
              <DialogTitle
                sx={{ border: '1px solid #e0e0e0', padding: '16px 24px' }}
              >
                {mode === 'create' ? 'Add New Card' : 'Edit Payment Method'}
              </DialogTitle>
              {config && (
                <DialogContent>
                  {tokenError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {tokenError}
                    </Alert>
                  )}
                  {createError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {formatQueryError(createError)}
                    </Alert>
                  )}
                  {editError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {formatQueryError(editError)}
                    </Alert>
                  )}
                  {editSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Successfully edited payment method!
                    </Alert>
                  )}
                  {mode === 'edit' && (
                    <Field
                      component={CheckboxWithLabel}
                      name="addNewCard"
                      type="checkbox"
                      Label={{ label: 'Edit Card Info' }}
                    />
                  )}

                  {/* {values.addNewCard && <StripeCreditCardForm />} */}
                  {((values.addNewCard && card && card.stripeId) || !card) && (
                    <StripeCreditCardForm />
                  )}
                  {values.addNewCard && card && card.braintreeId && (
                    <PaymentMethodForm
                      // disabled={!values.isNewPaymentMethod}
                      disabled={false}
                      clientToken={config.braintreeClientToken}
                      hostedFieldsInstance={hostedFieldsInstance}
                      setHostedFieldsInstance={setHostedFieldsInstance}
                    />
                  )}

                  <Typography variant="h6" sx={{ my: 2 }}>
                    Billing Address
                  </Typography>
                  <BillingAddressForm disabled={isLoading} />
                </DialogContent>
              )}
              {!config && (
                <DialogContent>
                  <CircularProgress />
                </DialogContent>
              )}
              <DialogActions>
                <Button type="submit" variant="contained" disabled={isLoading}>
                  Save
                </Button>
                <Button onClick={handleClose}>Cancel</Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default AddCardDialog;
